import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { LocationService } from '../../services/location.service';


type ProductFormData = {
  name: string;
  originalPrice: number;
  discountPrice: number;
  expiryDate: string;
  startTime: string;
  endTime: string;
  quantity: number;
  description: string;
  nutritionInfo: string;
  location: string;
};

type AddressSuggestion = {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
};

export default function AddProduct() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [locationSuggestions, setLocationSuggestions] = useState<AddressSuggestion[]>([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const navigate = useNavigate();
  
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<ProductFormData>();
  const originalPrice = watch('originalPrice');
  const discountPrice = watch('discountPrice');
  const location = watch('location') || '';
  
  const calculateSavings = () => {
    if (!originalPrice || !discountPrice) return null;
    
    const savings = originalPrice - discountPrice;
    const percentage = (savings / originalPrice) * 100;
    return {
      amount: savings.toFixed(2),
      percentage: Math.round(percentage)
    };
  };
  
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setSelectedImage(file);
    
    // Create a preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };
  
  // Handle location search when user types in location field
  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (searchQuery.length >= 3) {
        try {
          const suggestions = await LocationService.getAddressSuggestions(searchQuery);
          setLocationSuggestions(suggestions);
          setShowLocationSuggestions(suggestions.length > 0);
        } catch (error) {
          console.error('Error fetching location suggestions:', error);
        }
      } else {
        setLocationSuggestions([]);
        setShowLocationSuggestions(false);
      }
    }, 500);

    return () => clearTimeout(searchTimeout);
  }, [searchQuery]);

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setValue('location', query);
    if (query !== location) {
      setSelectedLocationId('');
    }
  };

  const selectLocation = (suggestion: AddressSuggestion) => {
    setValue('location', suggestion.description);
    setSearchQuery(suggestion.description);
    setSelectedLocationId(suggestion.place_id);
    setShowLocationSuggestions(false);
  };
  
  const onSubmit = async (data: ProductFormData) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      let imageUrl = '';
      
      // Upload image if selected
      if (selectedImage) {
        // In a real app, this would be an API call
        // const uploadResult = await uploadProductImage(selectedImage);
        // imageUrl = uploadResult.url;
        imageUrl = 'https://via.placeholder.com/150';
      }
      
      // Get geocode data if needed
      let locationData = {};
      if (data.location) {
        try {
          if (selectedLocationId) {
            locationData = {
              location: data.location,
              place_id: selectedLocationId
            };
          } else {
            // If no specific place_id, try to geocode the address
            const geocodeResult = await LocationService.geocodeAddress(data.location);
            if (geocodeResult) {
              locationData = {
                location: geocodeResult.formatted_address,
                lat: geocodeResult.lat,
                lng: geocodeResult.lng
              };
            }
          }
        } catch (locError) {
          console.error('Geocoding error (non-fatal):', locError);
          // Continue with user-entered location
          locationData = { location: data.location };
        }
      }
      
      // Create product with image URL and location data
      const productData = {
        ...data,
        imageUrl,
        nutritionInfo: data.nutritionInfo ? JSON.parse(data.nutritionInfo) : {},
        // Format dates and times for API
        start_time: data.startTime ? `${data.expiryDate}T${data.startTime}:00` : null,
        end_time: data.endTime ? `${data.expiryDate}T${data.endTime}:00` : null,
        ...locationData
      };
      
      // In a real app, this would be an API call
      // await createProduct(productData);
      console.log('Product data:', productData);
      
      // Navigate back to products list
      navigate('/products');
    } catch (err: any) {
      setError(err.message || 'Failed to create product');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const savings = calculateSavings();
  
  return (
      <div className="space-y-6">
        <div className="flex items-center">
          <h1 className="text-xl font-medium text-navy-blue">Add Product</h1>
        </div>
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-navy-blue">Product Information</h2>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Product Name <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="name"
                      className={`shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.name ? 'border-red-300' : ''
                      }`}
                      {...register('name', { required: 'Product name is required' })}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>
                </div>
                
                <div className="sm:col-span-3">
                  <label htmlFor="originalPrice" className="block text-sm font-medium text-gray-700">
                    Original Price ($) <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="number"
                      id="originalPrice"
                      step="0.01"
                      min="0"
                      className={`shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.originalPrice ? 'border-red-300' : ''
                      }`}
                      {...register('originalPrice', { 
                        required: 'Original price is required',
                        valueAsNumber: true,
                        min: { value: 0, message: 'Price must be positive' }
                      })}
                    />
                    {errors.originalPrice && (
                      <p className="mt-1 text-sm text-red-600">{errors.originalPrice.message}</p>
                    )}
                  </div>
                </div>
                
                <div className="sm:col-span-3">
                  <label htmlFor="discountPrice" className="block text-sm font-medium text-gray-700">
                    Discount Price ($) <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="number"
                      id="discountPrice"
                      step="0.01"
                      min="0"
                      className={`shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.discountPrice ? 'border-red-300' : ''
                      }`}
                      {...register('discountPrice', { 
                        required: 'Discount price is required',
                        valueAsNumber: true,
                        min: { value: 0, message: 'Price must be positive' },
                        validate: value => !originalPrice || value <= originalPrice || 'Discount price must be less than original price'
                      })}
                    />
                    {errors.discountPrice && (
                      <p className="mt-1 text-sm text-red-600">{errors.discountPrice.message}</p>
                    )}
                  </div>
                </div>
                
                {savings && (
                  <div className="sm:col-span-6">
                    <div className="bg-green-50 rounded-md p-3">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-green-800">
                            Customers save ${savings.amount} ({savings.percentage}% off)
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="sm:col-span-3">
                  <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700">
                    Expiry Date <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="date"
                      id="expiryDate"
                      className={`shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.expiryDate ? 'border-red-300' : ''
                      }`}
                      {...register('expiryDate', { required: 'Expiry date is required' })}
                    />
                    {errors.expiryDate && (
                      <p className="mt-1 text-sm text-red-600">{errors.expiryDate.message}</p>
                    )}
                  </div>
                </div>
                
                <div className="sm:col-span-3">
                  <label htmlFor="startTime" className="block text-sm font-medium text-gray-700">
                    Available From
                  </label>
                  <div className="mt-1">
                    <input
                      type="time"
                      id="startTime"
                      className={`shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.startTime ? 'border-red-300' : ''
                      }`}
                      {...register('startTime')}
                    />
                    {errors.startTime && (
                      <p className="mt-1 text-sm text-red-600">{errors.startTime.message}</p>
                    )}
                  </div>
                </div>
                
                <div className="sm:col-span-3">
                  <label htmlFor="endTime" className="block text-sm font-medium text-gray-700">
                    Available Until
                  </label>
                  <div className="mt-1">
                    <input
                      type="time"
                      id="endTime"
                      className={`shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.endTime ? 'border-red-300' : ''
                      }`}
                      {...register('endTime')}
                    />
                    {errors.endTime && (
                      <p className="mt-1 text-sm text-red-600">{errors.endTime.message}</p>
                    )}
                  </div>
                </div>
                
                <div className="sm:col-span-3">
                  <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="number"
                      id="quantity"
                      min="0"
                      className={`shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.quantity ? 'border-red-300' : ''
                      }`}
                      {...register('quantity', { 
                        required: 'Quantity is required',
                        valueAsNumber: true,
                        min: { value: 0, message: 'Quantity must be positive' }
                      })}
                    />
                    {errors.quantity && (
                      <p className="mt-1 text-sm text-red-600">{errors.quantity.message}</p>
                    )}
                  </div>
                </div>
                
                <div className="sm:col-span-6">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="description"
                      rows={3}
                      className="shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-gray-300 rounded-md"
                      {...register('description')}
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Brief description of the product.
                  </p>
                </div>
                
                <div className="sm:col-span-6">
                  <label htmlFor="nutritionInfo" className="block text-sm font-medium text-gray-700">
                    Nutrition Information (JSON)
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="nutritionInfo"
                      rows={3}
                      className="shadow-sm focus:ring-sky-blue focus:border-sky-blue block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder='{"calories": 120, "protein": "2g", "fat": "0g", "carbs": "30g"}'
                      {...register('nutritionInfo', {
                        validate: value => {
                          if (!value) return true;
                          try {
                            JSON.parse(value);
                            return true;
                          } catch (e) {
                            return 'Invalid JSON format';
                          }
                        }
                      })}
                    />
                    {errors.nutritionInfo && (
                      <p className="mt-1 text-sm text-red-600">{errors.nutritionInfo.message}</p>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Enter nutrition information in JSON format.
                  </p>
                </div>
                
                <div className="sm:col-span-6">
                  <label className="block text-sm font-medium text-gray-700">
                    Product Image
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    {imagePreview ? (
                      <div className="space-y-1 text-center">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="mx-auto h-32 w-32 object-cover rounded-md"
                        />
                        <div className="flex text-sm text-gray-600">
                          <label
                            htmlFor="file-upload"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-sky-blue hover:text-teal focus-within:outline-none"
                          >
                            <span>Change image</span>
                            <input 
                              id="file-upload" 
                              name="file-upload" 
                              type="file" 
                              className="sr-only" 
                              accept="image/*"
                              onChange={handleImageChange}
                            />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1 text-center">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                          <label
                            htmlFor="file-upload"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-sky-blue hover:text-teal focus-within:outline-none"
                          >
                            <span>Upload a file</span>
                            <input 
                              id="file-upload" 
                              name="file-upload" 
                              type="file" 
                              className="sr-only" 
                              accept="image/*"
                              onChange={handleImageChange}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, GIF up to 10MB
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Location Field */}
                <div className="sm:col-span-6">
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                    Location
                  </label>
                  <div className="mt-1 relative">
                    <div className="flex rounded-md shadow-sm">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                        <MapPinIcon className="h-5 w-5" />
                      </span>
                      <input
                        type="text"
                        id="location"
                        className="flex-1 block w-full rounded-none rounded-r-md border-gray-300 focus:border-sky-blue focus:ring-sky-blue sm:text-sm"
                        placeholder="Enter a location or address"
                        {...register('location')}
                        onChange={handleLocationChange}
                      />
                    </div>

                    {/* Location suggestions dropdown */}
                    {showLocationSuggestions && (
                      <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1 text-sm">
                        {locationSuggestions.map((suggestion) => (
                          <div
                            key={suggestion.place_id}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => selectLocation(suggestion)}
                          >
                            <p className="font-medium">{suggestion.structured_formatting.main_text}</p>
                            <p className="text-gray-500 text-xs">{suggestion.structured_formatting.secondary_text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Enter the physical location where this product is available
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-navy-blue hover:bg-sky-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : 'Save Product'}
            </button>
          </div>
        </form>
      </div>
  );
}
